import { Children, cloneElement, createElement, Fragment, isValidElement, type ReactElement, type ReactNode } from 'react';

/** A catalogue message's tags and what each becomes: `{ b: (text) => <strong>{text}</strong> }`. */
export type RichTags = Record<string, (children: ReactNode) => ReactNode>;

/** Where a link in a message may lead: https, or a path here. Anything else, say `javascript:`, is refused. */
export function safeHref(href: unknown, allow: readonly string[] = []): boolean {
  if (typeof href !== 'string') return false;
  const here = 'https://relative.invalid';
  try {
    const url = new URL(href, here);
    return url.protocol === 'https:' || allow.includes(url.protocol);
  } catch { return false; }
}

/** A tag's element, at any depth, with each link to somewhere a message may not lead reduced to its plain words. */
function checked(node: ReactNode, allow: readonly string[]): ReactNode {
  if (!isValidElement(node)) return node;
  const { href, children } = (node as ReactElement<{ href?: unknown; children?: ReactNode }>).props;
  const inner = children === undefined ? undefined : Children.map(children, (child) => checked(child, allow));
  if (node.type === 'a' && !safeHref(href, allow)) return createElement(Fragment, null, inner);
  return inner === undefined ? node : cloneElement(node, undefined, inner);
}

/**
 * The text of a message with its tags as elements, never as HTML. For a sentence that can't be split into pieces.
 * Each tag is a name the caller mapped. Anything else stays text. Tags are read while the values are still held out of
 * `text` (the translator's `held`), and `fill` puts them back after, so a value is only ever words.
 */
export function richParts(text: string, tags: RichTags, fill: (part: string) => string = (part) => part, allow: readonly string[] = []): ReactNode[] {
  const names = Object.keys(tags).join('|');
  if (!names) return [fill(text)];
  const out: ReactNode[] = [];
  const open = new RegExp(`<(${names})>`, 'g');
  let at = 0;
  for (let m = open.exec(text); m; m = open.exec(text)) {
    const close = text.indexOf(`</${m[1]}>`, open.lastIndex);
    if (close < 0) continue;
    if (m.index > at) out.push(fill(text.slice(at, m.index)));
    const words = fill(text.slice(open.lastIndex, close));
    out.push(createElement(Fragment, { key: m.index }, checked(tags[m[1]](words), allow)));
    at = close + m[1].length + 3;
    open.lastIndex = at;
  }
  if (at < text.length) out.push(fill(text.slice(at)));
  return out;
}
